import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { Card } from '@amore/ui/components/card'
import { ProgressRing } from '@amore/ui/components/progress-ring'
import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { formatMoney } from '@/lib/format'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import { PromptList, useAddPromptTask } from '@/patterns/prompts'
import { ScreenSection } from '@/patterns/screen'
import { useToast } from '@/shell/toaster'
import { budgetTotals } from '../budget/model'
import { guestTotals } from '../guests/model'
import { overviewPrompts } from './model'

export function OverviewNumbers() {
  const data = useWedding()
  const budget = budgetTotals(data)
  const guests = guestTotals(data)
  const done = data.tasks.filter((task) => task.done).length
  const active = data.suppliers.filter((supplier) => supplier.status !== 'cancelled')
  const agreed = active.filter(
    (supplier) => supplier.status === 'agreed' || supplier.status === 'booked',
  ).length
  const replied = guests.confirmed + guests.declined
  const metrics = [
    {
      to: '/tasks',
      label: 'Úkoly',
      value: done,
      max: data.tasks.length,
      headline: `${done} z ${data.tasks.length} hotovo`,
      description: `${data.tasks.length - done} zbývá`,
      accessible: `Úkoly: ${done} z ${data.tasks.length} hotovo`,
    },
    {
      to: '/guests',
      label: 'Hosté',
      value: replied,
      max: guests.total,
      headline: `${replied} z ${guests.total} odpovědělo`,
      description: `${guests.confirmed} přijde · ${guests.pending} čeká`,
      accessible: `Hosté: ${replied} z ${guests.total} odpovědělo, ${guests.confirmed} přijde`,
    },
    {
      to: '/budget',
      label: 'Rozpočet',
      value: budget.planned,
      max: budget.total ?? 0,
      headline: budget.total === null ? 'Zatím nenastavený' : formatMoney(budget.planned),
      description:
        budget.total === null ? 'Nastavte si svůj limit' : `z ${formatMoney(budget.total)}`,
      accessible: `Rozpočet: plánované náklady ${formatMoney(budget.planned)}, limit ${budget.total === null ? 'nenastavený' : formatMoney(budget.total)}`,
      danger: budget.unallocated !== null && budget.unallocated < 0,
      empty: budget.total === null,
    },
    {
      to: '/suppliers',
      label: 'Dodavatelé',
      value: agreed,
      max: active.length,
      headline: `${agreed} z ${active.length} domluveno`,
      description: `${active.length - agreed} v jednání`,
      accessible: `Dodavatelé: ${agreed} z ${active.length} domluveno`,
    },
  ]
  return (
    <ScreenSection title="Vaše přípravy v kostce">
      <Card padding="none" className="overflow-hidden">
        <div className="grid grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => {
            const percentage = metric.max > 0 ? Math.round((metric.value / metric.max) * 100) : 0
            return (
              <Link
                key={metric.to}
                to={metric.to}
                className="group flex min-w-0 items-center gap-3 border-border p-4 outline-offset-[-3px] transition-colors odd:border-r hover:bg-accent/20 focus-visible:outline-2 focus-visible:outline-ring max-xl:nth-[n+3]:border-t xl:not-last:border-r"
              >
                <ProgressRing
                  value={metric.value}
                  max={metric.max}
                  size="sm"
                  aria-label={metric.accessible}
                  className={cn(
                    'size-14 sm:size-16',
                    metric.danger && '[&_circle:last-child]:stroke-danger-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'rounded-full bg-card px-0.5 text-sm font-semibold tabular-nums',
                      metric.danger && 'text-danger-foreground',
                    )}
                  >
                    {metric.empty || metric.max === 0 ? '—' : `${percentage} %`}
                  </span>
                </ProgressRing>
                <div className="min-w-0 space-y-1">
                  <h3 className="text-sm font-semibold group-hover:text-action">{metric.label}</h3>
                  <p
                    className={cn(
                      'break-words text-xs leading-5',
                      metric.danger && 'text-danger-foreground',
                    )}
                  >
                    {metric.headline}
                  </p>
                  <p className="text-xs leading-5 text-muted-foreground">{metric.description}</p>
                </div>
              </Link>
            )
          })}
        </div>
      </Card>
    </ScreenSection>
  )
}

export function OverviewPrompts() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const addTask = useAddPromptTask()
  const { showToast } = useToast()
  if (!canEdit) return null
  return (
    <PromptList
      title="Stojí za to ověřit"
      visible={4}
      prompts={overviewPrompts(data).map((prompt) => ({
        code: prompt.code,
        title: prompt.title,
        hint: `${prompt.source} · ${prompt.hint}`,
        action: prompt.task ? (
          <Button
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={() => {
              if (prompt.task)
                addTask(prompt.task.title, {
                  labels: prompt.task.labels,
                  context: prompt.task.context,
                })
            }}
          >
            Přidat úkol
          </Button>
        ) : (
          <Link
            to={prompt.to}
            className="inline-flex min-h-9 items-center gap-2 rounded-lg px-3 text-sm font-semibold text-action outline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
          >
            {prompt.label}
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        ),
      }))}
      onHide={(code) => {
        update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
        showToast({
          title: 'Doporučení skryto',
          action: {
            label: 'Vrátit zpět',
            altText: 'Znovu zobrazit doporučení',
            onClick: () =>
              update((current) => ({
                ...current,
                hiddenPrompts: current.hiddenPrompts.filter((item) => item !== code),
              })),
          },
        })
      }}
    />
  )
}
